// ── HTML-in-canvas bake (Chrome flag: chrome://flags/#canvas-draw-element) ───────────────────────────────
// Ported from the BIRTVY perf bench (2026-07-05). `ctx.drawElementImage()` paints REAL HTML (real CSS,
// layout, fonts) into a 2D canvas using the browser's OWN pipeline — perfect fidelity, and unlike the SVG
// <foreignObject> path it does NOT taint the canvas and does NOT fail/blank the way foreignObject decode does
// (that is exactly the empty-block bug in the compositor's thumbnails). The element must be a DOM child of the
// canvas, so we keep one offscreen `<canvas layoutsubtree>` with a host div, drop a slide's HTML in it,
// requestPaint(), and snapshot it in onpaint. If the API isn't present (flag off), `isHtmlBakeAvailable()`
// returns false and callers keep their SVG fallback.
//
// This is a canvas-owned duplicate of the bench's rig (the lab is untouched).

const BOARD_W = 1920
const BOARD_H = 1080
const TEX_W = 512
const TEX_H = Math.round((TEX_W * BOARD_H) / BOARD_W) // 288

type ElBakeCtx = CanvasRenderingContext2D & {
  drawElementImage(el: Element, dx: number, dy: number, dw?: number, dh?: number): unknown
}
interface BakeRig {
  canvas: HTMLCanvasElement & { requestPaint(): void; onpaint: (() => void) | null }
  ctx: ElBakeCtx
  host: HTMLDivElement
}

let rig: BakeRig | null = null
let probed = false

/** Build the offscreen `<canvas layoutsubtree>` rig with a host child. ON SCREEN (origin in the viewport) but
 *  ~invisible: drawElementImage snapshots the PAINT pipeline, and Chrome culls paint for content parked far
 *  off-screen → a blank capture. Near-zero opacity keeps it painted while imperceptible. Returns null when the
 *  API isn't available (flag off / unsupported). */
function makeBakeRig(): BakeRig | null {
  if (typeof document === 'undefined') return null
  const c = document.createElement('canvas')
  c.width = BOARD_W
  c.height = BOARD_H
  c.setAttribute('layoutsubtree', '')
  c.style.cssText =
    'position:fixed;left:0;top:0;width:1920px;height:1080px;opacity:0.0015;pointer-events:none;z-index:2147483000'
  const host = document.createElement('div')
  host.style.cssText = 'width:1920px;height:1080px;position:relative;overflow:hidden;background:#ffffff'
  c.appendChild(host)
  document.body.appendChild(c)
  const ctx = c.getContext('2d') as (CanvasRenderingContext2D & { drawElementImage?: unknown }) | null
  const ok =
    !!ctx &&
    typeof ctx.drawElementImage === 'function' &&
    typeof (c as unknown as { requestPaint?: unknown }).requestPaint === 'function'
  if (!ok) {
    c.remove()
    return null
  }
  return { canvas: c as BakeRig['canvas'], ctx: ctx as ElBakeCtx, host }
}

/** Whether the HTML-in-canvas API is usable in this browser right now (lazily probes once). */
export function isHtmlBakeAvailable(): boolean {
  if (!probed) {
    probed = true
    rig = makeBakeRig()
    console.log(`[canvas-lab] HTML-in-canvas bake: ${rig ? 'ON (drawElementImage)' : 'OFF — flag not enabled, using SVG fallback'}`)
  }
  return rig !== null
}

/** True if the capture is effectively all white (paint got culled / nothing drew) — a sampled scan. */
function looksBlank(base: HTMLCanvasElement): boolean {
  try {
    const cx = base.getContext('2d')
    if (!cx) return false
    const d = cx.getImageData(0, 0, base.width, base.height).data
    let hits = 0
    for (let p = 0; p < d.length; p += 4 * 53) {
      if (d[p] < 236 || d[p + 1] < 236 || d[p + 2] < 236) {
        if (++hits > 4) return false
      }
    }
    return true
  } catch {
    return false
  }
}

/** Capture `html` as REAL HTML into a fresh origin-clean 1920×1080 canvas: inject it in the rig host,
 *  neutralize editables, force layout, requestPaint(), and drawElementImage the host in onpaint. Resolves the
 *  canvas, or null on timeout/error. */
function captureHtml(rig: BakeRig, html: string): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    rig.host.innerHTML = html
    for (const ce of rig.host.querySelectorAll('[contenteditable]')) {
      ce.setAttribute('contenteditable', 'false')
      ce.setAttribute('spellcheck', 'false')
    }
    void rig.host.offsetHeight // force layout before the paint snapshot
    let done = false
    const finish = (out: HTMLCanvasElement | null) => {
      if (done) return
      done = true
      rig.canvas.onpaint = null
      clearTimeout(timer)
      resolve(out)
    }
    const timer = setTimeout(() => finish(null), 1000)
    rig.canvas.onpaint = () => {
      try {
        rig.ctx.setTransform(1, 0, 0, 1, 0, 0)
        rig.ctx.clearRect(0, 0, BOARD_W, BOARD_H)
        rig.ctx.drawElementImage(rig.host, 0, 0)
        const out = document.createElement('canvas')
        out.width = BOARD_W
        out.height = BOARD_H
        out.getContext('2d')!.drawImage(rig.canvas, 0, 0)
        finish(out)
      } catch (err) {
        console.warn('[canvas-lab] html-in-canvas capture failed', err)
        finish(null)
      }
    }
    rig.canvas.requestPaint()
  })
}

// One rig, one onpaint at a time → captures MUST serialize. This chain queues concurrent callers onto the
// single rig so the compositor's parallel thumbnail pump can call us freely.
let chain: Promise<unknown> = Promise.resolve()

/** Bake `html` to a 512×288 base canvas via HTML-in-canvas (captured at 1920 for crispness, then downscaled).
 *  Returns null if the API is unavailable, the capture times out, or it came back blank — callers then fall
 *  back to their SVG path. `html` should already be sanitized by the caller. */
export async function bakeHtmlToBase(html: string): Promise<HTMLCanvasElement | null> {
  if (!isHtmlBakeAvailable() || !rig) return null
  const r = rig
  const run = chain.then(() => captureHtml(r, html))
  chain = run.catch(() => {})
  const cap = await run
  if (!cap) return null
  try {
    const base = document.createElement('canvas')
    base.width = TEX_W
    base.height = TEX_H
    const cx = base.getContext('2d')!
    cx.imageSmoothingEnabled = true
    cx.imageSmoothingQuality = 'high'
    cx.drawImage(cap, 0, 0, TEX_W, TEX_H)
    return looksBlank(base) ? null : base
  } catch {
    return null
  }
}
